/* eslint-disable no-param-reassign, no-use-before-define --
   moved as is from StartServerModal: it writes the chosen map onto the caller's server object */
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useI18n } from 'vue-i18n';
import {
  latencyLabel, loadServerLocations, serverLocations, sortByLatency,
} from '../utils/server_locations';
import {
  loadUserPreferences, preferencesLoading, resolveServerLocation, setServerLocation,
} from '../utils/user_preferences';

// The Start server modal's state and API calls, shared by the classic StartServerModal and the
// v5 V5StartServerModal. `props` are the modal's props (show, server, showMapSelector); `emit`
// gets close, failed and success.
export default function useStartServer(props, emit) {
  const { t } = useI18n();
  const store = useStore();
  const location = ref(null);
  const activeServer = computed(() => store.state.activeServer);
  const serverStarted = ref(false);
  const copySucceededVal = ref(false);
  const auth = useAuth();
  const user = ref(auth.user());
  const selectedLocation = ref(null);
  const servers = computed(() => sortByLatency(serverLocations.value));
  const selectedMap = ref(null);
  const modLoading = ref(false);
  const startingServer = ref(false);
  const taskLaunchLoading = ref(false);
  const selectedDifficulty = ref(0); // 0 = Smart, 1 = Pro
  const difficulties = computed(() => [
    { value: 1, label: t('play.start_server.difficulty_pro'), description: t('play.start_server.difficulty_pro_description') },
    { value: 0, label: t('play.start_server.difficulty_smart'), description: t('play.start_server.difficulty_smart_description') },
  ]);

  const cs2ServerDisabled = computed(() => {
    const startCs2Server = store.state.siteSettings?.find(
      (setting) => setting.name === 'start_cs2_server',
    )?.enabled;
    if (typeof startCs2Server !== 'undefined') return !startCs2Server;
    return false;
  });

  function copySucceeded() {
    copySucceededVal.value = true;
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('play.shared.link_copied'),
    });
    setTimeout(() => {
      copySucceededVal.value = false;
      store.commit('hideMessage');
    }, 3000);
  }

  function capitalizeFirstLetter(string) {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
  }

  // Shared, loaded once per page load. This modal is mounted eagerly on several
  // pages, so its own request used to race the layout's ping results.
  loadServerLocations().then(getServerLocation);

  const maps = computed(() => store.state.modInfo?.mods
    ?.find((mod) => mod?.name?.toLowerCase() === props.server?.mode?.toLowerCase())
    ?.cs_maps?.filter((map) => map.cs2 === true)
    ?.sort((a, b) => a.friendly_name?.toLowerCase().localeCompare(b.friendly_name?.toLowerCase())));

  const storedLocationParsed = computed(() => resolveServerLocation(servers.value));

  function closeModal() {
    emit('close');
  }

  function reportError() {
    emit('failed');
  }

  function reportSuccess() {
    emit('success');
  }

  function getServerLocation() {
    // The stored location arrives with the preferences fetch, and these are plain
    // refs that nothing re-runs. Resolving before it lands would pin the modal --
    // and the server it starts -- to the first location in the list.
    loadUserPreferences().then(() => {
      const stored = resolveServerLocation(servers.value);

      if (stored) {
        location.value = stored;
        selectedLocation.value = stored;
      }
    });
  }

  // The catalogue is replaced wholesale as each ping lands, so re-resolve the
  // held location by id to pick up its ms without losing the choice.
  //
  // Resolves, rather than only refreshing, when nothing is held: the shared load
  // never rejects, so a transient failure would otherwise leave this modal with no
  // location for the rest of the page.
  watch(servers, (list) => {
    if (!list?.length) return;

    const refresh = (current) => (
      current
        ? list.find((server) => server.id === current.id) ?? current
        : resolveServerLocation(list)
    );

    location.value = refresh(location.value);
    selectedLocation.value = refresh(selectedLocation.value);
  });

  function updateSelectedLocation(server) {
    selectedLocation.value = server;
    location.value = server;
    setServerLocation(server);
  }

  function updateMap(map) {
    selectedMap.value = map;
    props.server.cs_map = map;
  }

  function updateDifficulty(difficulty) {
    selectedDifficulty.value = difficulty;
  }

  function getDifficultyLabel(value) {
    const difficulty = difficulties.value.find((d) => d.value === value);
    return difficulty ? difficulty.label : t('play.start_server.difficulty_smart');
  }

  // Show difficulty selector when use_difficulty is set to 1 or when it's a BLAST task
  const shouldShowDifficultySelector = computed(() => (
    props.server?.content?.task_list_type === 'playlist'
    && props.server?.content?.use_difficulty === 1
  ));

  // Bootcamp / exam task launches pass routine_launch_allow_overrides: false
  const routineLaunchAllowsOverrides = computed(
    () => props.server?.content?.routine_launch_allow_overrides !== false,
  );

  const showOverrideOptions = computed(() => routineLaunchAllowsOverrides.value);

  // Routine launch: backend expects exactly 3 values — task_list_id, override_map_id,
  // override_preset_id (-1 = no override)
  const isRoutineLaunch = computed(() => !!(
    props.server?.content?.task_list_id
      && props.server?.content?.type === 'task'
  ));

  // Maps from Quickfire mod (for override map selector)
  const routineMaps = computed(() => {
    const mods = store.state.modInfo?.mods || [];
    const quickfire = mods.find((mod) => mod.name.toLowerCase() === 'quickfire');
    const list = (quickfire?.cs_maps || []).filter((m) => m.cs2 && m.id != null);
    const name = (m) => m.friendly_name || m.name || '';
    return [...list].sort((a, b) => name(a).localeCompare(name(b)));
  });

  const routinePresets = ref([]);
  const overrideMapId = ref(-1);
  const overridePresetId = ref(-1);
  // Coach routines send the map override as a NAME, the routines list as a
  // numeric id. Kept apart so the name survives `routineMaps` loading late.
  const pendingOverrideMapName = ref(null);

  function loadRoutinePresets() {
    if (!isRoutineLaunch.value || !routineLaunchAllowsOverrides.value) return;
    axios
      .get('/presets', { headers: { 'X-TEAM-ID': store.state.activeTeam?.id } })
      .then((res) => {
        routinePresets.value = Array.isArray(res.data) ? res.data : [];
      })
      .catch(() => {
        routinePresets.value = [];
      });
  }

  function setOverrideMap(mapOrId) {
    pendingOverrideMapName.value = null;
    if (mapOrId == null || mapOrId === -1) {
      overrideMapId.value = -1;
      return;
    }
    overrideMapId.value = typeof mapOrId === 'object' ? mapOrId.id : mapOrId;
  }

  function setOverridePreset(presetOrId) {
    if (presetOrId == null || presetOrId === -1) {
      overridePresetId.value = -1;
      return;
    }
    overridePresetId.value = typeof presetOrId === 'object' ? presetOrId.id : presetOrId;
  }

  // What both launch paths send. Falls back to the name when the panel's map
  // list isn't loaded, which used to drop the override silently.
  const routineOverrideMapName = computed(() => {
    if (!isRoutineLaunch.value || !routineLaunchAllowsOverrides.value) return null;
    if (overrideMapId.value >= 0) {
      return routineMaps.value.find((m) => m.id === overrideMapId.value)?.name ?? null;
    }
    return pendingOverrideMapName.value;
  });

  const routineOverridePresetId = computed(() => {
    if (!isRoutineLaunch.value || !routineLaunchAllowsOverrides.value) return null;
    return overridePresetId.value >= 0 ? overridePresetId.value : null;
  });

  // `modInfo` can land after the modal opens, so this runs on seed and again
  // when the map list arrives.
  function resolvePendingOverrideMap() {
    if (!pendingOverrideMapName.value || overrideMapId.value >= 0) return;

    const match = routineMaps.value.find((m) => m.name === pendingOverrideMapName.value);
    if (match) overrideMapId.value = match.id;
  }

  watch(routineMaps, resolvePendingOverrideMap);

  function seedRoutineOverrides() {
    if (!routineLaunchAllowsOverrides.value) {
      overrideMapId.value = -1;
      overridePresetId.value = -1;
      pendingOverrideMapName.value = null;
      return;
    }

    overrideMapId.value = props.server?.content?.override_map_id ?? -1;
    overridePresetId.value = props.server?.content?.override_preset_id
      ?? props.server?.content?.preset_loadout_id
      ?? -1;
    pendingOverrideMapName.value = props.server?.content?.map_overrider ?? null;
    resolvePendingOverrideMap();
    loadRoutinePresets();
  }

  function launchTask() {
    if (!activeServer.value?.id || activeServer.value?.status !== 'online') {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.start_server.server_not_online'),
      });
      return;
    }

    if (!props.server?.content?.task_list_id) {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.start_server.task_list_missing'),
      });
      return;
    }
    if (
      !isRoutineLaunch.value
      && !props.server?.content?.task_list_section_id
    ) {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.start_server.task_info_missing'),
      });
      return;
    }

    taskLaunchLoading.value = true;
    const taskListId = props.server.content.task_list_id;
    const sectionId = props.server.content.task_list_section_id ?? -1;
    const taskId = props.server.content.id ?? -1;
    const taskListDifficulty = props.server.content.task_list_difficulty ?? -1;
    const isExam = props.server.content.is_exam ? 1 : 0;
    const presetDifficultyId = -1;
    const presetLoadoutId = routineOverridePresetId.value ?? -1;
    const mapOverride = routineOverrideMapName.value ?? -1;
    const useDifficulty = isRoutineLaunch.value
      ? (props.server.content.use_difficulty ?? 1)
      : selectedDifficulty.value;

    // Backend: task_list_id, task_list_section_id, task_id, task_list_difficulty, is_exam,
    // preset_difficulty_id, preset_loadout_id, map_override (string name), use_difficulty
    const value = [
      taskListId, sectionId, taskId, taskListDifficulty, isExam,
      presetDifficultyId, presetLoadoutId, mapOverride, useDifficulty,
    ].join(', ');

    axios({
      url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
      method: 'POST',
      data: {
        command: 'launch_task',
        value,
        is_client_command: false,
      },
      headers: {
        'X-TEAM-ID': store.getters.activeTeam.id,
      },
    })
      .then(() => {
        store.commit('showMessage', {
          messageType: 'success',
          messageText: t('play.start_server.routine_launched'),
        });
        taskLaunchLoading.value = false;
        serverStarted.value = true;
        setTimeout(() => {
          store.commit('hideMessage');
        }, 2000);
      })
      .catch(() => {
        taskLaunchLoading.value = false;
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('play.start_server.command_failed'),
        });
      });
  }

  function launchMode() {
    modLoading.value = true;

    if (!props.server?.mode || !selectedMap.value?.name) {
      console.error('Server mode or map name is undefined');
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.start_server.mode_or_map_missing'),
      });
      modLoading.value = false;
      props.showMapSelector = true;
      return Promise.resolve(false);
    }

    // Resolves true once the server took the change, false on failure (the v5 dialog uses it)
    return axios({
      url: `/cs_servers/${activeServer.value.id}/exec_server_command`,
      method: 'POST',
      params: {
        command: 'change_map_and_mod',
        value: `${selectedMap.value.name}, ${props.server.mode}`,
      },
      headers: {
        'X-TEAM-ID': store.getters.activeTeam.id,
      },
    })
      .then(() => true)
      .catch(() => {
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('play.shared.server_unreachable'),
        });
        setTimeout(() => {
          store.commit('hideMessage');
        }, 3000);
        return false;
      })
      .finally(() => {
        modLoading.value = false;
      });
  }

  function startServer() {
    startingServer.value = true;
    // if server is active, just launch the mode for the selected map
    if (activeServer.value?.id) {
      if (props.server.cs_map) {
        updateMap(props.server.cs_map);
        launchMode();
        startingServer.value = false;
        return;
      }

      updateMap(maps.value[0]);
      props.showMapSelector = true;
      startingServer.value = false;
      return;
    }
    props.showMapSelector = false;

    const currentTeam = user.value?.teams.find(
      (team) => team.id === store.state.activeTeam.id,
    );

    if (!currentTeam.can_start_server && user.value?.access_level > 2) {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: t('play.start_server.switch_team_subscription'),
      });
      closeModal();
      startingServer.value = false;
      return;
    }

    if (!location.value && selectedLocation.value) {
      location.value = selectedLocation.value;
      setServerLocation(selectedLocation.value);
    }

    // Nothing resolved a location -- the list never arrived, or the preferences
    // saying which of it to use have not yet. Say so instead of throwing on
    // location.value.id and leaving the modal on its spinner.
    if (!location.value) {
      store.commit('showMessage', {
        messageType: 'error',
        messageText: preferencesLoading.value
          ? t('play.shared.locations_still_loading')
          : t('play.start_server.locations_failed'),
      });
      startingServer.value = false;
      return;
    }

    axios({
      url: '/cs_servers/start_new_server',
      method: 'POST',
      timeout: 120000,
      headers: {
        'X-TEAM-ID': store.state.activeTeam.id,
      },
      data: {
        server_location_id: location.value.id,
        game: 'cs2',
        betaServer: false,
        secureServer: false,
        is_assessment: false,
        launch_settings: {
          mod: getMod(props?.server?.mode) || null,
          map: props?.server?.cs_map?.name || null,
          map_overrider: isRoutineLaunch.value
            ? routineOverrideMapName.value
            : props?.server?.content?.map_overrider ?? null,
          preset_loadout_id: isRoutineLaunch.value
            ? routineOverridePresetId.value
            : props?.server?.content?.preset_loadout_id ?? null,
          subject_id: props?.server?.content?.id || null,
          subject_type: getSubjectType(props?.server?.mode) || null,
          task_list_id:
            props?.server?.content?.task_list_id || props?.server?.content?.id || null,
          task_list_section_id: props?.server?.content?.task_list_section_id || null,
          // ?? not ||: beginner is 0, and null makes the server load an empty task list.
          task_list_difficulty: props?.server?.content?.task_list_difficulty ?? null,
          time_to_kill: props?.server?.time_to_kill || null,
          is_exam: props?.server?.content?.is_exam || false,
          round_id: props?.server?.content?.round_id || null,
          preset_id: props?.server?.content?.preset_id || null,
          use_difficulty: shouldShowDifficultySelector.value
            ? !!selectedDifficulty.value
            : false,
        },
      },
    })
      .then(({ data }) => {
        store.commit('setActiveServer', data);
        startingServer.value = false;
        serverStarted.value = true;
        reportSuccess();
      })
      .catch((err) => {
        startingServer.value = false;
        console.log(err);
        reportError();
      });
  }

  function getMod(mode) {
    switch (mode) {
      case 'Creator Studio':
        return 'creatorstudio';
      case 'VPL':
        return 'vpl';
      case 'Grenade Groups':
        return 'nadr';
      case 'Strategies':
        return 'nadr';
      default:
        return mode;
    }
  }

  function getSubjectType(mode) {
    if (props.server.content?.type) {
      return props.server.content?.type;
    }

    switch (mode) {
      case 'Creator Studio':
        return 'custom_set';
      case 'Grenade Groups':
        return 'grenades';
      case 'Strategies':
        return 'strat';
      case 'VPL':
        return 'strat';
      case 'Restrat':
        return 'match';
      default:
        return null;
    }
  }

  watch(
    () => maps.value,
    () => {
      if (selectedMap.value === null && !props.server.cs_map?.name) {
        updateMap(maps.value[0]);
      }
      if (selectedMap.value === null && props.server.cs_map?.name) {
        const chosenMap = maps.value.find((map) => map.name === props.server.cs_map.name);
        if (chosenMap) {
          updateMap(chosenMap);
        }
      }
    },
  );

  watch(
    () => props.show,
    (newVal) => {
      if (!newVal) {
        serverStarted.value = false;
      } else if (isRoutineLaunch.value) {
        seedRoutineOverrides();
      }
    },
  );

  return {
    user,
    cs2ServerDisabled,
    closeModal,
    isRoutineLaunch,
    activeServer,
    serverStarted,
    showOverrideOptions,
    overrideMapId,
    routineMaps,
    setOverrideMap,
    overridePresetId,
    routinePresets,
    setOverridePreset,
    launchTask,
    taskLaunchLoading,
    shouldShowDifficultySelector,
    getDifficultyLabel,
    selectedDifficulty,
    difficulties,
    updateDifficulty,
    capitalizeFirstLetter,
    selectedMap,
    maps,
    updateMap,
    launchMode,
    modLoading,
    selectedLocation,
    storedLocationParsed,
    latencyLabel,
    servers,
    updateSelectedLocation,
    startServer,
    startingServer,
    location,
    copySucceededVal,
    copySucceeded,
  };
}
